"use client";

import { useEffect, useRef } from "react";
import { replaceIconLigatures } from "@/src/utils/html-parser";

export default function DynamicPage({ htmlContent }: { htmlContent: string }) {
  const containerRef = useRef<HTMLDivElement>(null);

  const cleanHtml = (html: string) => {
    return html.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      "",
    );
  };

  const sanitizedHtml = replaceIconLigatures(cleanHtml(htmlContent));

  useEffect(() => {
    if (!containerRef.current) return;

    const doc = new DOMParser().parseFromString(htmlContent, "text/html");

    const scripts: (HTMLScriptElement & { isExternal: boolean })[] = [];

    doc.querySelectorAll("script").forEach((script) => {
      const isExternal = !!script.src;
      const newScript = document.createElement("script");

      if (isExternal) {
        newScript.src = script.src;
      } else {
        newScript.textContent = script.textContent || "";
      }

      scripts.push(Object.assign(newScript, { isExternal }));
      script.remove();
    });

    const loadScriptsSequentially = async () => {
      for (const script of scripts) {
        await new Promise<void>((resolve, reject) => {
          if (script.isExternal) {
            script.onload = () => resolve();
            script.onerror = () => reject();
            document.body.appendChild(script);
          } else {
            document.body.appendChild(script);
            resolve();
          }
        });
      }
    };

    loadScriptsSequentially().catch(console.error);
  }, []);

  return (
    <div
      ref={containerRef}
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
}
